import { cloneDeep, isEmpty, isEqual } from 'lodash-es';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { track } from '~/src/dash/analytics/analytics';
import { getValidDateGroup, IAnalytics, queryAnalytics } from '~/src/dash/api/console-api';
import { IFocusedInput } from '~/src/dash/components/calendar/calendar-wrapper';
import { DrillHorizontalBarChart } from '~/src/dash/components/charts/DrillHorizontalBarChart';
import { DashboardBreadCrumbs, IDashboardCrumb } from '~/src/dash/components/DashboardBreadCrumbs';
import {
  IObjectWithDefault,
  ITableCol,
  ITableGroupBy,
  ITableGroupBys,
  ITableMetric,
  ITableMetrics,
  ORDER_DIRECTION,
  TableGroupByKey,
  ValueType,
  valueTypes,
} from '~/src/dash/components/tables/common';
import { DrillTable } from '~/src/dash/components/tables/DrillTable';
import { genTimezoneList } from '~/src/dash/globals/timezone';
import {
  borderRadius,
  Panel,
  PanelBreak,
  Panels,
  Row,
  Rows,
  spacing,
  usePublicaTranslation,
} from '~/src/dash/publicaUI';
import { IMenuList } from '~/src/dash/types/menuList';
import {
  findCompareEndDate,
  findCompareStartDate,
  generateCalendarProps,
  generateCompareCalendarProps,
} from '~/src/dash/utils/calendar';
import { publicaColors } from '~/src/dash/utils/colors';
import { formatDateEndDay, formatDateStartDay } from '~/src/dash/utils/date';
import { DateGroupDays } from '~/src/dash/api/console-api';
import {
  getHeaderBidderAdaptersForPublisher,
  IHeaderBidderAdapter,
  unlistedHeaderBidderAdapters,
} from '~/src/dash/utils/header-bidder-adapters';
import {
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import { obfuscateDataLabel } from '~/src/dash/utils/obfuscate';
import { hasRoleSuperUser } from '~/src/dash/utils/roles';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import { BrandsDashboardFilters } from '~/src/dash/views/brands-dashboard/BrandsDashboard/BrandsDashboardFilters';
import {
  BrandsDashboardFilterKey,
  BrandsDashboardMetricKey,
  IBrandsDashboardFilters,
  IBrandsDashboardRow,
  IChartDataItem,
  IDrillItem,
  IFiltersState,
} from '~/src/dash/views/brands-dashboard/BrandsDashboard/common/types';
import {
  brandsDashboardConfig,
  formatComparisonKeys,
  formatFilter,
} from '~/src/dash/views/brands-dashboard/config';
import { BrandsDashboardContext } from '~/src/dash/views/brands-dashboard/contexts';
import { dashboardMainChartColorBank } from '~/src/dash/views/common/dashboards-consts';
import { ISingleSelectOption } from '~/src/dash/views/singleselect-view';
import {
  generateSnackbarState,
  ISnackbarState,
  SnackbarView,
} from '~/src/dash/views/snackbar-view';
import Skeleton from '@mui/material/Skeleton';
import { TopBar } from '~/src/dash/views/topbar/TopBar';
import { resetMomentLocale, updateMomentLocale } from '~/src/locales/momentCfg';
import { DemandSources } from '~/src/dash/globals/demand-sources';

// get config
const cfg = brandsDashboardConfig;

const isFiltersEmpty = (filters: IFiltersState) => {
  if (isEmpty(filters)) {
    return true;
  }
  return Object.values(filters).every((filter) => isEmpty(filter.list));
};

const generateDemandSourceMap = (demandSourceAdapters: IHeaderBidderAdapter[]) => {
  const demandSourceMap = {} as Record<string, string>;
  demandSourceAdapters.forEach((adapter) => {
    demandSourceMap[adapter.key] = adapter.text;
  });
  return demandSourceMap;
};

const convertAggDataExchangeRate = (
  data: IAnalytics[],
  ExchangeValueToUSD: number,
): IAnalytics[] => {
  for (const datum of data) {
    datum.Revenue = datum.Revenue * ExchangeValueToUSD;
    datum.TotalImpEcpm = datum.TotalImpEcpm * ExchangeValueToUSD;
    datum.AvgImpECPM = datum.AvgImpECPM * ExchangeValueToUSD;
  }
  return data;
};

const extractAllAPIKeysFromGroupBysAndMetrics = (
  groupBys: Partial<ITableGroupBys>,
  metrics: Partial<ITableMetrics>,
) => {
  const keys = {} as Record<string, boolean>;
  Object.values(groupBys).forEach((groupBy) => {
    keys[groupBy.apiKey] = true;
    keys[groupBy.displayKey] = true;
  });
  Object.values(metrics).forEach((metric) => {
    keys[metric.apiKey] = true;
  });
  return Object.keys(keys);
};

const getDrillFilter = (drillItems: IDrillItem[]) => {
  let filters = {} as Record<string, any>;
  drillItems.forEach((item) => {
    filters = { ...filters, ...item.drillFilter };
  });
  return filters;
};

interface IFilterDataParams {
  additionalKeys?: string[];
  data: IAnalytics[];
  groupByAPIKey: string;
  isComparisonData?: boolean;
  keys: string[];
  demandSourceMap: Record<string, string>;
}

const filterData = ({
  additionalKeys = [],
  data,
  groupByAPIKey,
  keys,
  demandSourceMap,
}: IFilterDataParams) => {
  const updatedKeys = [...keys, ...additionalKeys];
  const orderedDataKeys: string[] = [];
  const filteredData: Record<string, IAnalytics> = {};
  data.forEach((item) => {
    const row = {} as IAnalytics;
    updatedKeys.forEach((key) => {
      if (key !== 'BidderTypeName') {
        row[key] = item[key];
      }
      if (key === 'BidderType') {
        // this fixes an edge case scenario due to a hardcoded transform on the backend
        if (item[key] === 'VGI CTV') {
          row.BidderTypeName = 'VGI CTV';
          row.BidderType = DemandSources.nexstar;
        } else {
          row.BidderTypeName = demandSourceMap[item[key]] || item[key];
        }
      }
    });
    // this fixes an edge case scenario due to a hardcoded transform on the backend
    const key = item[groupByAPIKey] === 'VGI CTV' ? DemandSources.nexstar : item[groupByAPIKey];
    filteredData[key as string] = row;
    orderedDataKeys.push(key as string);
  });
  return { data: filteredData, orderedDataKeys };
};

const extractNonComparisonMetricKeysFromMetrics = (metrics: Partial<ITableMetrics>): string[] => {
  const keys: string[] = [];
  Object.values(metrics).forEach((metric) => {
    if (!metric.isCompareValue) {
      keys.push(metric.label);
    }
  });
  return keys;
};

interface IFormatMainData {
  [key: string]: Record<string, IAnalytics>;
}

interface IFormatDataParams {
  primaryData: IPrimaryData;
  comparisonData?: Record<string, IAnalytics>;
  isComparedToEnabled?: boolean;
}

const formatData = ({
  primaryData,
  comparisonData = {},
  isComparedToEnabled = false,
}: IFormatDataParams) => {
  const formattedData: IFormatMainData = {};
  const { data, orderedDataKeys } = primaryData;
  orderedDataKeys.forEach((key) => {
    if (isComparedToEnabled) {
      formattedData[key] = {
        main: data[key],
        compare: comparisonData[key],
      };
    } else {
      formattedData[key] = { main: data[key] };
    }
  });
  return {
    data: formattedData,
    orderedDataKeys: primaryData.orderedDataKeys,
  };
};

const getSelectedListItem = (list: IMenuList) => {
  return Object.values(list).find(({ selected }) => selected === true);
};

const getFilterParams = (filters: IFiltersState, configFilters: IBrandsDashboardFilters) => {
  // type 'any' here to account for all possible value formats
  const filterParams: Record<string, any> = {};
  Object.values(configFilters).forEach((filter) => {
    const { paramKey, menuTitle } = filter;
    const formattedFilter = formatFilter(menuTitle, filters[menuTitle] && filters[menuTitle].list);
    if (filterParams[paramKey]) {
      const dedupIDMap: any = {};
      if (formattedFilter !== '') {
        formattedFilter.split(',').forEach((id: string) => (dedupIDMap[id] = true));
      }
      filterParams[paramKey].split(',').forEach((id: string) => (dedupIDMap[id] = true));
      const paramKeyFilterCopy = Object.keys(dedupIDMap).join(',');
      filterParams[paramKey] = paramKeyFilterCopy;
    } else {
      filterParams[paramKey] = formattedFilter;
    }
  });
  return filterParams;
};

const generateGroupBySelectItemsFromConfig = (
  groupBys: Partial<ITableGroupBys>,
): ISingleSelectOption[] => {
  const items = [] as ISingleSelectOption[];
  Object.values(groupBys).forEach((groupBy) => {
    items.push({
      label: groupBy.pluralLabel,
      value: groupBy.label,
      tKey: groupBy.tKey || '',
    });
  });
  return items;
};

const generateOrderBySelectItemsFromConfig = (
  orderBys: Partial<ITableMetrics>,
): ISingleSelectOption[] => {
  const items = [] as ISingleSelectOption[];
  Object.values(orderBys).forEach((orderBy) => {
    if (!orderBy.isCompareValue) {
      items.push({
        label: orderBy.label,
        value: orderBy.label,
      });
    }
  });
  return items;
};

const extractDefaultFromArrayOfObjects = (array: IObjectWithDefault[]) => {
  return array.find(({ isDefault }) => isDefault) || array[0];
};

const generateCols = (
  groupBy: ITableGroupBy | undefined,
  metrics: ITableMetric[],
  isComparedToEnabled: boolean,
): ITableCol[] => {
  if (!groupBy) {
    return [];
  }
  const cols = [
    {
      label: groupBy.label,
      alias: groupBy.alias || '',
      apiKey: groupBy.apiKey,
      displayKey: groupBy.displayKey,
      requestKey: groupBy.requestKey,
      defaultOrderDirection: ORDER_DIRECTION.ASC,
      isSelectable: true,
      isTableControllingSort: !!groupBy.isTableControllingSort,
      valueType: valueTypes.NAME,
      tooltipMessage: groupBy.tooltipMessage || '',
      headerStyle: groupBy.headerStyle || {},
      tKey: groupBy.tKey || '',
    } as ITableCol,
  ];
  metrics.forEach((metric) => {
    if ((metric.isCompareValue && isComparedToEnabled) || !metric.isCompareValue) {
      cols.push({
        label: metric.label,
        alias: metric.alias || '',
        apiKey: metric.apiKey,
        displayKey: metric.apiKey,
        requestKey: metric.requestKey,
        defaultOrderDirection: metric.defaultOrderDirection,
        isSelectable: metric.isSelectable,
        isTableControllingSort: !!metric.isTableControllingSort,
        isCompareValue: !!metric.isCompareValue,
        valueType: metric.valueType,
        tooltipMessage: metric.tooltipMessage || '',
        headerStyle: metric.headerStyle || {},
      });
    }
  });
  return cols;
};

const calculateCompareValue = (
  mainValue: number,
  comparisonValue: number,
  valueType: ValueType,
): number | undefined => {
  const { PERCENT_CHANGE } = valueTypes;
  switch (valueType) {
    case PERCENT_CHANGE:
      return ((mainValue - comparisonValue) / comparisonValue) * 100;
    default:
      return;
  }
};

const generateRows = (
  cols: ITableCol[],
  mainData: IMainData,
  isComparedToEnabled: boolean,
  groupByLabel: string,
  username: string,
) => {
  const rows = [] as Array<Record<string, any>>;
  const { data, orderedDataKeys } = mainData;
  orderedDataKeys.forEach((key, idx) => {
    const mainItem = data[key].main;
    const comparisonItem = data[key].compare;
    const row = {} as any;
    cols.forEach((col, i) => {
      if (i === 0) {
        row.Name = obfuscateDataLabel(
          username,
          mainItem[col.displayKey] as string,
          groupByLabel,
          `${idx + 1}`,
        );
        row.ID = mainItem[col.apiKey];
      } else {
        if (isComparedToEnabled && col.isCompareValue) {
          if (comparisonItem) {
            const value = calculateCompareValue(
              mainItem[col.apiKey] as number,
              comparisonItem[col.apiKey] as number,
              col.valueType,
            );
            row[col.label] = value;
          } else {
            row[col.label] = '';
          }
        } else {
          row[col.label] = mainItem[col.apiKey];
        }
      }
    });
    rows.push(row);
  });
  return rows;
};

const limitRowCount = (totalRows: number, limit: number) => {
  return totalRows > limit ? limit : totalRows;
};

const generateChartData = (
  mainData: IMainData,
  groupBy: ITableGroupBy,
  orderBy: ITableMetric,
  isComparedToEnabled: boolean,
  colorBank: string[],
  username: string,
) => {
  const chartData: Record<string, IChartDataItem> = {};
  const { data, orderedDataKeys } = mainData;
  let n = 0;
  orderedDataKeys.forEach((key, i) => {
    n++;
    const item = data[key];
    const ID = item.main[groupBy.apiKey] as string;
    const primaryValue = item.main[orderBy.apiKey] as number;
    const compareValue =
      isComparedToEnabled && !isEmpty(item.compare)
        ? {
            comparisonValue: (item.compare[orderBy.apiKey] as number) || undefined,
            percentChange:
              calculateCompareValue(
                primaryValue,
                item.compare[orderBy.apiKey] as number,
                valueTypes.PERCENT_CHANGE,
              ) || undefined,
          }
        : {};
    chartData[ID] = {
      id: ID,
      name: obfuscateDataLabel(
        username,
        item.main[groupBy.displayKey] as string,
        groupBy.label,
        `${n}`,
      ),
      valueType: orderBy.valueType,
      primaryValue,
      ...compareValue,
      color: colorBank[i % colorBank.length],
    };
  });
  return chartData;
};

const filterChartData = (orderedDataKeys: string[], chartData: Record<string, IChartDataItem>) => {
  const result: IChartDataItem[] = [];
  orderedDataKeys.forEach((key) => {
    result.push(chartData[key]);
  });
  return result;
};

const generateBreadCrumbItems = (drillItems: IDrillItem[]) => {
  const items: IDashboardCrumb[] = [];
  drillItems.forEach((item) => {
    items.push({
      sourceLabel: item.groupByKey,
      itemLabel: item.name,
      tKey: item.tKey,
    });
  });
  return items;
};

interface IMainData {
  data: { [key: string]: Record<string, IAnalytics> };
  orderedDataKeys: string[];
}

interface IPrimaryData {
  data: Record<string, IAnalytics>;
  orderedDataKeys: string[];
}

// set needed config consts
const dataKeys = extractAllAPIKeysFromGroupBysAndMetrics(cfg.groupBys, cfg.mainMetrics);
const allMetricKeys = Object.keys(cfg.mainMetrics);
const nonComparisonMetricKeys = extractNonComparisonMetricKeysFromMetrics(cfg.mainMetrics);

// set needed consts
// booleans created for readability of queryData
const isComparisonQuery = true;
const isDrillQuery = true;

export const BrandsDashboard = () => {
  // custom hook consts
  const { session, user, isSuperUser } = useBaseStore();
  const { t } = usePublicaTranslation();
  // states and refs
  const initialLoadStart = moment().unix();
  const isInitialized = useRef(false);
  const isMounted = useRef(false);
  // loading
  const [dataLoadState, setDataLoadState] = useState(LoadingStateLoading);
  // data
  const [primaryData, setPrimaryData] = useState({} as IPrimaryData);
  const [comparisonKeys, setComparisonKeys] = useState<string[]>([]);
  const [comparisonData, setComparisonData] = useState<Record<string, IAnalytics>>({});
  const [mainData, setMainData] = useState({} as IMainData);
  // track handlers
  const [handleType, setHandleType] = useState('');
  // timezones
  const [timezone, setTimezone] = useState(session.publisher.Timezone);
  const [timezonesList, setTimezonesList] = useState<IMenuList>(
    genTimezoneList(session.publisher.Timezone),
  );
  // calendars
  const [rangeSelectedStr, setRangeSelectedStr] = useState('Last 7 Days');
  const [calendar, setCalendar] = useState(generateCalendarProps(session.publisher.Timezone));
  const [isNewCalReady, setIsNewCalReady] = useState(false);
  const [calFocusedInput, setCalFocusedInput] = useState<IFocusedInput>(null);
  const [compareCalendar, setCompareCalendar] = useState(
    generateCompareCalendarProps(session.publisher.Timezone),
  );
  const [compFocusedInput, setCompFocusedInput] = useState<IFocusedInput>(null);
  const [densityRangeAgg, setDensityRangeAgg] = useState(DateGroupDays);
  const [isComparedToEnabled, setIsComparedToEnabled] = useState(true);
  // this ensures comparison calendar is up to date before api call
  const [isCompCalUpdated, setIsCompCalUpdated] = useState(false);
  // filters
  const [filters, setFilters] = useState({} as IFiltersState);
  // group by
  const [currentGroupBy, setCurrentGroupBy] = useState(
    extractDefaultFromArrayOfObjects(Object.values(cfg.groupBys)) as ITableGroupBy,
  );
  // order by
  const [currentOrderBy, setCurrentOrderBy] = useState(
    extractDefaultFromArrayOfObjects(Object.values(cfg.mainMetrics)) as ITableMetric,
  );
  // table loading
  const [areRowsUpdating, setAreRowsUpdating] = useState(true);
  // table specific
  const [displayedTableRowIDs, setDisplayedTableRowIDs] = useState<string[]>([]);
  // drills
  const [isDrillActive, setIsDrillActive] = useState(false);
  const [drillGroupBy, setDrillGroupBy] = useState(currentGroupBy);
  const [drillItems, setDrillItems] = useState([] as IDrillItem[]);
  // cols and rows
  const [cols, setCols] = useState<ITableCol[]>(
    generateCols(currentGroupBy, Object.values(cfg.mainMetrics), isComparedToEnabled),
  );
  const [rows, setRows] = useState<Array<Record<string, any>>>([]);
  // main bar chart
  const [isChartUpdating, setIsChartUpdating] = useState(true);
  const [chartData, setChartData] = useState<Record<string, IChartDataItem>>({});
  const [displayedChartData, setDisplayedChartData] = useState<IChartDataItem[]>([]);
  // snackbar state
  const [snackbar, setSnackbar] = useState<ISnackbarState>(generateSnackbarState());
  // other consts
  const publisherID = session.publisher.ID;
  const { ExchangeValueToUSD, CurrencyType } = session.publisher.ExchangeRate;

  const { data } = primaryData;

  updateMomentLocale(t);

  // useEffects
  // this resets it back to Sun-Sat on unmount
  useEffect(() => {
    return () => {
      resetMomentLocale();
    };
  }, []);

  // make the initial api calls on load
  useEffect(() => {
    queryData({});
    track('Brands Dashboard - View', { isSuperUser }, user.user);
  }, []);

  // handles timezone changes
  useEffect(() => {
    if (isMounted.current) {
      const selectedTimezone = getSelectedListItem(timezonesList);
      setTimezone(selectedTimezone ? selectedTimezone.ID : session.publisher.Timezone);
    }
  }, [timezonesList]);

  // this resets the calendar on timezone change
  useEffect(() => {
    if (isMounted.current) {
      unstable_batchedUpdates(() => {
        setCalendar(generateCalendarProps(timezone));
        setCompareCalendar(generateCompareCalendarProps(timezone));
      });
    }
  }, [timezone]);

  // handles calendar changes
  useEffect(() => {
    if (
      isMounted.current &&
      handleType !== 'handleDensityRangeChangeAgg' &&
      handleType !== 'handleCalendarFocusedInputChange'
    ) {
      const updatedCompStartDate = findCompareStartDate({
        timezone,
        startDate: calendar.displayStartDate,
        endDate: calendar.endDate,
        rangeSelected: rangeSelectedStr,
      });
      const updatedCompEndDate = findCompareEndDate({
        timezone,
        startDate: calendar.displayStartDate,
        endDate: calendar.displayEndDate,
        rangeSelected: rangeSelectedStr,
      });
      const clonedCompareCalendar = cloneDeep(compareCalendar);
      clonedCompareCalendar.startDate = updatedCompStartDate;
      clonedCompareCalendar.endDate = updatedCompEndDate;
      clonedCompareCalendar.displayStartDate = updatedCompStartDate;
      clonedCompareCalendar.displayEndDate = updatedCompEndDate;
      unstable_batchedUpdates(() => {
        setCompareCalendar(clonedCompareCalendar);
        setIsCompCalUpdated(true);
      });
    }
  }, [timezone, calendar, densityRangeAgg]);

  // if filters change...
  useEffect(() => {
    if (isMounted.current) {
      if (drillItems.length > 0) {
        queryData({ isDrillQuery });
      } else {
        queryData({});
      }
    }
  }, [filters, timezone]);

  // if the calendar changes...
  useEffect(() => {
    if (
      (isMounted.current &&
        handleType !== 'handleCalendarDateChange' &&
        handleType !== 'handleCalendarFocusedInputChange' &&
        handleType !== 'handleChangeAggRangeSelected') ||
      (isCompCalUpdated && isNewCalReady)
    ) {
      if (drillItems.length > 0) {
        queryData({ isDrillQuery });
      } else {
        queryData({});
      }
      unstable_batchedUpdates(() => {
        setIsNewCalReady(false);
        setIsCompCalUpdated(false);
      });
    }
  }, [isCompCalUpdated, densityRangeAgg, isNewCalReady]);

  // if the group by or order by changes...
  useEffect(() => {
    if (isMounted.current) {
      resetDrill();
    }
  }, [currentOrderBy, currentGroupBy]);

  // get the primary data keys
  useEffect(() => {
    if (isMounted.current) {
      unstable_batchedUpdates(() => {
        setComparisonKeys(primaryData.orderedDataKeys || []);
        if (!isComparedToEnabled) {
          setMainData(formatData({ primaryData }));
        }
      });
    }
  }, [primaryData]);

  // get comparison data
  useEffect(() => {
    if (isMounted.current && isComparedToEnabled) {
      if (comparisonKeys.length > 0) {
        queryData({ isComparisonQuery });
      } else {
        setComparisonData({});
      }
    }
  }, [comparisonKeys]);

  // format data for comparison
  useEffect(() => {
    if (isMounted.current) {
      setMainData(
        formatData({
          primaryData,
          comparisonData,
          isComparedToEnabled,
        }),
      );
    }
  }, [comparisonData]);

  // Notes: This prevents an unnecessary api call when disabling comparison data
  useEffect(() => {
    if (isMounted.current && handleType === 'handleCompareEnabled') {
      if (isComparedToEnabled) {
        resetDrill();
      } else {
        setComparisonData({});
      }
    }
  }, [isComparedToEnabled]);

  useEffect(() => {
    if (isMounted.current) {
      if (drillItems.length > 0) {
        queryData({ isDrillQuery });
      } else {
        queryData({});
      }
    }
  }, [drillItems]);

  useEffect(() => {
    if (isMounted.current) {
      setCols(
        isDrillActive
          ? generateCols(
              cfg.groupBys[drillGroupBy.label],
              Object.values(cfg.mainMetrics),
              isComparedToEnabled,
            )
          : generateCols(currentGroupBy, Object.values(cfg.mainMetrics), isComparedToEnabled),
      );
    }
  }, [isDrillActive, currentGroupBy, drillGroupBy, isComparedToEnabled]);

  useEffect(() => {
    if (isMounted.current && cols.length > 0 && !isEmpty(mainData)) {
      unstable_batchedUpdates(() => {
        setRows(
          generateRows(cols, mainData, isComparedToEnabled, drillGroupBy.label, user.user.Username),
        );
        setChartData(
          generateChartData(
            mainData,
            drillGroupBy,
            currentOrderBy,
            isComparedToEnabled,
            Object.values(dashboardMainChartColorBank),
            user.user.Username,
          ),
        );
      });
    }
  }, [cols, mainData, isComparedToEnabled, drillGroupBy, currentOrderBy, isComparedToEnabled]);

  useEffect(() => {
    if (isMounted.current && !isEmpty(chartData) && displayedTableRowIDs.length > 0) {
      const filteredChartData = filterChartData(displayedTableRowIDs, chartData);
      setDisplayedChartData(filteredChartData);
    }
  }, [displayedTableRowIDs, chartData]);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
    }
  });

  // calendar functions
  const handleChangeAggRangeSelected = (
    rangeSelected: string,
    isCompareCalendar: boolean,
    applied?: boolean,
  ): void => {
    setHandleType('handleChangeAggRangeSelected');
    if (isCompareCalendar) {
      const clonedCompareCalendar = cloneDeep(compareCalendar);
      if (applied) {
        clonedCompareCalendar.rangeSelected = rangeSelected;
        clonedCompareCalendar.rangeDisplaySelected = rangeSelected;
      } else {
        clonedCompareCalendar.rangeDisplaySelected = rangeSelected;
      }
      unstable_batchedUpdates(() => {
        setCompareCalendar(clonedCompareCalendar);
      });
    } else {
      const clonedCalendar = cloneDeep(calendar);
      if (applied) {
        clonedCalendar.rangeSelected = rangeSelected;
        clonedCalendar.rangeDisplaySelected = rangeSelected;
      } else {
        clonedCalendar.rangeDisplaySelected = rangeSelected;
      }
      unstable_batchedUpdates(() => {
        setRangeSelectedStr(rangeSelected);
        setCalendar(clonedCalendar);
      });
    }
  };

  // this causes a console error due to a bad setState within the nested component
  // no fix needed for now
  const handleCalendarDateChange = (
    isCompareCalendar: boolean,
  ): ((start: moment.Moment, end: moment.Moment, isCustom: boolean) => void) => {
    setHandleType('handleCalendarDateChange');
    track('Brands Dashboard - Use Calendar', { isSuperUser }, user.user);
    if (isCompareCalendar) {
      return (start: moment.Moment, end: moment.Moment, isCustom: boolean) => {
        if (isCustom) {
          handleChangeAggRangeSelected('Custom', true, true);
        }
        const clonedCompareCalendar = cloneDeep(compareCalendar);
        clonedCompareCalendar.displayStartDate = start;
        clonedCompareCalendar.displayEndDate = end;
        unstable_batchedUpdates(() => {
          setCompareCalendar(clonedCompareCalendar);
        });
      };
    } else {
      return (start: moment.Moment, end: moment.Moment, isCustom: boolean) => {
        if (isCustom) {
          handleChangeAggRangeSelected('Custom', false, true);
        }
        const clonedCalendar = cloneDeep(calendar);
        clonedCalendar.displayStartDate = start;
        clonedCalendar.displayEndDate = end;
        setCalendar(clonedCalendar);
      };
    }
  };

  const handleDateRangeChange = (
    start: moment.Moment,
    end: moment.Moment,
    isCompareCalendar: boolean,
  ): void => {
    setHandleType('handleDateRangeChange');
    const startDate = cloneDeep(start).startOf('day');
    const endDate = cloneDeep(end).endOf('day');
    if (isCompareCalendar) {
      const clonedCompareCalendar = cloneDeep(compareCalendar);
      clonedCompareCalendar.startDate = startDate;
      clonedCompareCalendar.endDate = endDate;
      unstable_batchedUpdates(() => {
        setCompareCalendar(clonedCompareCalendar);
        setDensityRangeAgg(getValidDateGroup(densityRangeAgg, start, end));
        setIsCompCalUpdated(true);
      });
    } else {
      const clonedCalendar = cloneDeep(calendar);
      clonedCalendar.startDate = startDate;
      clonedCalendar.endDate = endDate;
      unstable_batchedUpdates(() => {
        setCalendar(clonedCalendar);
        setDensityRangeAgg(getValidDateGroup(densityRangeAgg, start, end));
      });
    }
    setIsNewCalReady(true);
    track('Brands Dashboard - Use Calendar', { isSuperUser }, user.user);
  };

  const handleCalendarFocusedInputChange = (
    isCompareCalendar: boolean,
  ): ((focusedInput: IFocusedInput) => void) => {
    setHandleType('handleCalendarFocusedInputChange');
    if (isCompareCalendar) {
      return (focusedInput: IFocusedInput) => {
        const clonedCalendar = cloneDeep(calendar);
        const clonedCompareCalendar = cloneDeep(compareCalendar);
        if (focusedInput === null) {
          unstable_batchedUpdates(() => {
            setCalFocusedInput(null);
            setCompFocusedInput(null);
          });
          clonedCalendar.displayStartDate = calendar.startDate;
          clonedCalendar.displayEndDate = calendar.endDate;
          clonedCompareCalendar.displayStartDate = compareCalendar.startDate;
          clonedCompareCalendar.displayEndDate = compareCalendar.endDate;
        } else {
          unstable_batchedUpdates(() => {
            setCalFocusedInput(null);
            setCompFocusedInput(focusedInput);
          });
        }
      };
    } else {
      return (focusedInput: IFocusedInput) => {
        const clonedCalendar = cloneDeep(calendar);
        const clonedCompareCalendar = cloneDeep(compareCalendar);
        if (focusedInput === null) {
          unstable_batchedUpdates(() => {
            setCalFocusedInput(null);
            setCompFocusedInput(null);
          });
          clonedCalendar.displayStartDate = calendar.startDate;
          clonedCalendar.displayEndDate = calendar.endDate;
          clonedCompareCalendar.displayStartDate = compareCalendar.startDate;
          clonedCompareCalendar.displayEndDate = compareCalendar.endDate;
        } else {
          unstable_batchedUpdates(() => {
            setCalFocusedInput(focusedInput);
            setCompFocusedInput(null);
          });
        }
      };
    }
  };

  const handleCompareEnabled = () => {
    setHandleType('handleCompareEnabled');
    track(
      'Brands Dashboard - Click Compared To Checkbox',
      { isSuperUser, isComparedToEnabled: !isComparedToEnabled },
      user.user,
    );
    setIsComparedToEnabled(!isComparedToEnabled);
  };

  // timezone functions
  const handleTimezoneOnChange = (list: IMenuList) => {
    setHandleType('handleTimezoneOnChange');
    setTimezonesList(list);
    track('Brands Dashboard - Filters Timezone', { isSuperUser }, user.user);
  };

  // filter functions
  const handleFilterAccept = (menuTitle: BrandsDashboardFilterKey, list: IMenuList) => {
    setHandleType('handleFilterAccept');
    if (!filters[menuTitle] && isEmpty(list)) {
      return;
    }
    const clonedFilters = cloneDeep(filters);
    clonedFilters[menuTitle] = {
      menuTitle,
      list,
    };
    setFilters(clonedFilters);
    track('Brands Dashboard - Filters By', { isSuperUser, filter: menuTitle }, user.user);
  };

  const handleClearFilters = () => {
    setHandleType('handleClearFilters');
    unstable_batchedUpdates(() => {
      setFilters({} as IFiltersState);
    });
  };

  const handleGroupByOnChange = (_label: string, value: TableGroupByKey) => {
    const groupBy = cfg.groupBys[value];
    if (groupBy) {
      setHandleType('handleGroupByOnChange');
      unstable_batchedUpdates(() => {
        setCurrentGroupBy(groupBy);
        setDrillGroupBy(groupBy);
      });
      track('Brands Dashboard - Use Group By Filter', { isSuperUser, filter: value }, user.user);
    }
  };

  const handleOrderByOnChange = (_label: string, value: BrandsDashboardMetricKey) => {
    const metric = cfg.mainMetrics[value];
    if (metric) {
      setHandleType('handleOrderByOnChange');
      setCurrentOrderBy(metric);
      track('Brands Dashboard - Use Order By', { isSuperUser, filter: value }, user.user);
    }
  };

  const handleRowClick = (row: IBrandsDashboardRow | IChartDataItem) => {
    setHandleType('handleRowClick');
    if (!isEmpty(row)) {
      const nodeLevel = drillItems.length;
      const nextKey = cfg.groupBys[currentGroupBy.drillPath[nodeLevel]]?.label;
      const requestKey = nextKey ? cfg.groupBys[nextKey]?.requestKey : '';
      const clonedDrillItems = cloneDeep(drillItems);
      // we ignore type id/ID/name/Name because only one or the other set will exist
      // mui component needs uppercase; recharts component needs lowercase
      // @ts-ignore
      const id = row.ID || row.id;
      // @ts-ignore
      const name = row.Name || row.name;
      clonedDrillItems.push({
        id: id as string,
        name: name as string,
        apiKey: drillGroupBy.apiKey,
        matchKey: drillGroupBy.matchKey,
        requestKey: requestKey || '',
        groupByKey: drillGroupBy.label,
        tKey: drillGroupBy.tKey,
        nextGroupByKey: nextKey,
        drillFilter: {
          [drillGroupBy.matchKey]: formatComparisonKeys([id as string], drillGroupBy.apiKey),
        },
      });
      unstable_batchedUpdates(() => {
        setIsDrillActive(true);
        setDrillItems(clonedDrillItems);
      });
      track('Brands Dashboard - Click To Drilldown', { isSuperUser }, user.user);
    }
  };

  const handleTableDisplayChange = (rowIDs: string[]) => {
    // hard cap comparison for now
    const limitedRowIDs = rowIDs.length > 100 ? rowIDs.slice(0, 100) : rowIDs;
    if (!isEqual(limitedRowIDs, displayedTableRowIDs)) {
      setDisplayedTableRowIDs(rowIDs);
    }
  };

  const handleBreadCrumbClick = (index: number) => {
    const groupBy = cfg.groupBys[drillItems[index]?.groupByKey];
    if (groupBy) {
      setHandleType('handleBreadCrumbClick');
      const clonedDrillItems = cloneDeep(drillItems);
      unstable_batchedUpdates(() => {
        setDrillItems(clonedDrillItems.slice(0, index));
        setDrillGroupBy(groupBy);
      });
      track('Brands Dashboard - Breadcrumbs Navigation', { isSuperUser }, user.user);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({
      message: '',
      error: false,
      warning: false,
    });
  };

  const resetDrill = () => {
    unstable_batchedUpdates(() => {
      setDrillItems([]);
      setIsDrillActive(false);
      setDrillGroupBy(currentGroupBy);
    });
  };

  interface IQueryDataParams {
    isComparisonQuery?: boolean;
    isDrillQuery?: boolean;
  }

  // query for primary data
  const queryData = ({ isComparisonQuery = false, isDrillQuery = false }: IQueryDataParams) => {
    // reset load states
    if (!isComparisonQuery) {
      unstable_batchedUpdates(() => {
        setDataLoadState(LoadingStateLoading);
        setAreRowsUpdating(true);
        setIsChartUpdating(true);
      });
    }
    // query calendar
    const startDate = !isComparisonQuery
      ? formatDateStartDay(calendar.startDate)
      : formatDateStartDay(compareCalendar.startDate);
    const endDate = !isComparisonQuery
      ? formatDateEndDay(calendar.endDate)
      : formatDateEndDay(compareCalendar.endDate);

    // more params
    const grouping = densityRangeAgg;
    const filterParams = getFilterParams(filters, { ...cfg.filters, ...cfg.dynamicFilters });
    const currentDrillItem = drillItems[drillItems.length - 1];
    const groupBy =
      currentDrillItem && currentDrillItem.requestKey
        ? currentDrillItem.requestKey
        : currentGroupBy.requestKey;
    const orderBy = currentOrderBy.requestKey;

    let comparisonFilter = {};
    if (isComparisonQuery && !isDrillActive) {
      comparisonFilter = {
        [currentGroupBy.matchKey]: formatComparisonKeys(comparisonKeys, currentGroupBy.apiKey),
      };
    } else if (isComparisonQuery && isDrillActive) {
      comparisonFilter = {
        [drillGroupBy.matchKey]: formatComparisonKeys(comparisonKeys, drillGroupBy.apiKey),
      };
    }

    const drillFilter = drillItems ? getDrillFilter(drillItems) : {};
    // run query
    queryAnalytics({
      query: {
        graph_id: cfg.mainData.graphID,
        site_ids: '',
        bidder_ids: '',
        start_date: startDate,
        end_date: endDate,
        timezone,
        grouping,
        ...filterParams,
        publisher_id: publisherID,
        groupbyoptions: isComparisonQuery && isDrillActive ? drillGroupBy.requestKey : groupBy,
        report_order_by: orderBy,
        limit: cfg.table.totalLimit,
        offset: 0,
        order_direction: currentOrderBy.defaultOrderDirection,
        ...comparisonFilter,
        ...drillFilter,
      },
    })
      .then((data) => {
        let queryFailed = false;
        if (data.length === 0) {
          queryFailed = true;
          unstable_batchedUpdates(() => {
            setDataLoadState(LoadingStateNoData);
            setAreRowsUpdating(false);
            setIsChartUpdating(false);
            if (isComparisonQuery) {
              setComparisonData({});
            } else {
              setPrimaryData({
                data: {},
                orderedDataKeys: [],
              });
            }
          });
        }
        if (queryFailed && !isComparisonQuery) {
          return;
        }
        // convert all monetary values with publisher exchange rate
        const responseData = convertAggDataExchangeRate(data, ExchangeValueToUSD);

        const demandSourceAdapters = [
          ...getHeaderBidderAdaptersForPublisher(
            '',
            false,
            session.publisher,
            hasRoleSuperUser(user.user),
          ),
          ...unlistedHeaderBidderAdapters,
        ];

        const demandSourceMap = generateDemandSourceMap(demandSourceAdapters);
        const groupByAPIKey =
          currentDrillItem && currentDrillItem?.nextGroupByKey
            ? cfg.groupBys[currentDrillItem?.nextGroupByKey]?.apiKey
            : isDrillActive
            ? drillGroupBy.apiKey
            : currentGroupBy.apiKey;

        const filteredData = filterData({
          additionalKeys: !isComparisonQuery ? ['TotalCount'] : [],
          data: responseData,
          groupByAPIKey: groupByAPIKey || '',
          keys: dataKeys,
          demandSourceMap,
        });

        const groupBy =
          currentDrillItem && currentDrillItem?.nextGroupByKey
            ? cfg.groupBys[currentDrillItem.nextGroupByKey]
            : null;

        unstable_batchedUpdates(() => {
          setDataLoadState(LoadingStateHasData);
          if (isDrillQuery && currentDrillItem && currentDrillItem?.nextGroupByKey && groupBy) {
            setDrillGroupBy(groupBy);
          }
          if (!isComparisonQuery) {
            setPrimaryData(filteredData);
          } else {
            setComparisonData(filteredData.data);
          }
          if (!isComparedToEnabled || isComparisonQuery) {
            unstable_batchedUpdates(() => {
              setAreRowsUpdating(false);
              setIsChartUpdating(false);
            });
          }
          if (!isInitialized.current) {
            track(
              'Brands Dashboard - Initial Data Load Time',
              { isSuperUser, initialLoadTime: moment().unix() - initialLoadStart },
              user.user,
            );
            isInitialized.current = true;
          }
        });
      })
      .catch((err) => {
        console.error(err);
        unstable_batchedUpdates(() => {
          setDataLoadState(LoadingStateNoData);
          setAreRowsUpdating(false);
          setIsChartUpdating(false);
          setSnackbar({
            message: t('SnackbarErrorRetrievingData'),
            error: true,
            warning: false,
          });
        });
      });
  };

  return (
    <BrandsDashboardContext.Provider value={{ cfg }}>
      <Panels maxWidth vertical relative minWidthMain>
        <Panel
          largeHorizontalPadding
          verticalPadding
          style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}
        >
          <TopBar title='Brands Dashboard' enableUINotices tKey={'BrandsDashboard'} />
        </Panel>
        <Panel scroll paddingTop={spacing.small}>
          <Panels max vertical relative largeHorizontalPadding>
            <Panel paper marginTop={spacing.base}>
              {mainData.orderedDataKeys !== undefined ? (
                <BrandsDashboardFilters
                  user={user}
                  session={session}
                  // timezone props
                  timezone={timezone}
                  timezonesList={timezonesList}
                  handleTimezoneOnChange={handleTimezoneOnChange}
                  // calendar props
                  range1Selected={rangeSelectedStr}
                  range1DisplaySelected={rangeSelectedStr}
                  calStartDate={calendar.startDate}
                  calEndDate={calendar.endDate}
                  calDisplayStartDate={calendar.displayStartDate}
                  calDisplayEndDate={calendar.displayEndDate}
                  calFocusedInput={calFocusedInput}
                  // compare calendar props
                  range2Selected={compareCalendar.rangeSelected}
                  range2DisplaySelected={compareCalendar.rangeDisplaySelected}
                  range2TKey={compareCalendar.tKey}
                  compStartDate={compareCalendar.startDate}
                  compEndDate={compareCalendar.endDate}
                  compDisplayStartDate={compareCalendar.displayStartDate}
                  compDisplayEndDate={compareCalendar.displayEndDate}
                  compFocusedInput={compFocusedInput}
                  // calendar functions
                  handleChangeAggRangeSelected={handleChangeAggRangeSelected}
                  handleCalendarDateChange={handleCalendarDateChange}
                  handleDateRangeChange={handleDateRangeChange}
                  handleCalendarFocusedInputChange={handleCalendarFocusedInputChange}
                  // comparison toggle
                  isComparedToEnabled={isComparedToEnabled}
                  handleCompareEnabled={handleCompareEnabled}
                  // filters
                  filters={Object.values(cfg.filters)}
                  handleClearFilters={handleClearFilters}
                  isClearFiltersEnabled={!isFiltersEmpty(filters)}
                  // filter pill functions
                  handleFilterAccept={handleFilterAccept}
                  // view by select
                  currentGroupBy={currentGroupBy}
                  groupBySelectItems={generateGroupBySelectItemsFromConfig(cfg.groupBys)}
                  handleGroupByOnChange={handleGroupByOnChange}
                  currentOrderBy={currentOrderBy}
                  orderBySelectItems={generateOrderBySelectItemsFromConfig(cfg.mainMetrics)}
                  handleOrderByOnChange={handleOrderByOnChange}
                />
              ) : (
                <div style={{ padding: spacing.small }}>
                  <Skeleton
                    animation='wave'
                    width='100%'
                    style={{ borderRadius: borderRadius.base }}
                  />
                </div>
              )}
            </Panel>
            {/* MAIN SECTION */}
            <Panel>
              <DashboardBreadCrumbs
                items={generateBreadCrumbItems(drillItems)}
                onClick={handleBreadCrumbClick}
                initialMessageTKey='BrandsDashboardDrillMessage'
              />
            </Panel>
            <Panel
              paper
              style={{
                margin: '16px 0px',
                paddingBottom: spacing.small,
              }}
            >
              <Rows>
                {/* CHART */}
                <Row noPadding>
                  <DrillHorizontalBarChart
                    dataLoadState={dataLoadState}
                    data={displayedChartData}
                    eCurrencyType={CurrencyType}
                    isComparedToEnabled={isComparedToEnabled}
                    title={`Top ${drillGroupBy.pluralLabel} by ${currentOrderBy.label}`}
                    isChartUpdating={isChartUpdating}
                    handleBarClick={handleRowClick}
                    selectedDateRange={rangeSelectedStr}
                    titleTKey={'TopItemByAltItem'}
                    titleTProps={{
                      item: drillGroupBy.tKey ? t(drillGroupBy.tKey) : drillGroupBy.pluralLabel,
                      altItem: currentOrderBy.tKey ? t(currentOrderBy.tKey) : currentOrderBy.label,
                    }}
                  />
                </Row>
                {/* TABLE */}
                <Row style={{ paddingBottom: spacing.small }}>
                  <DrillTable
                    cols={cols}
                    rows={rows}
                    metricKeys={isComparedToEnabled ? allMetricKeys : nonComparisonMetricKeys}
                    metricValueTypes={cfg.mainMetrics}
                    eCurrencyType={CurrencyType}
                    orderBy={currentOrderBy}
                    orderDirection={currentOrderBy.defaultOrderDirection}
                    totalRows={
                      !isEmpty(data)
                        ? limitRowCount(Object.values(data)[0].TotalCount as number, 100)
                        : 0
                    }
                    rowsPerPageOptions={cfg.table.rowsPerPageOptions}
                    rowsPerPage={cfg.table.defaultRowsPerPage}
                    areRowsUpdating={areRowsUpdating}
                    handleRowClick={handleRowClick}
                    areRowsClickable={drillItems.length < currentGroupBy.drillPath.length}
                    handleTableDisplayChange={handleTableDisplayChange}
                    areRowsAllData
                  />
                </Row>
              </Rows>
            </Panel>
            <PanelBreak />
          </Panels>
        </Panel>
        <SnackbarView
          autoHideDuration={12000}
          open={!!snackbar.message}
          error={!!snackbar.error}
          onClose={handleSnackbarClose}
          messageColor={''}
          backgroundColor={''}
          closeColor={''}
          warning={!!snackbar.warning}
        >
          {snackbar.message}
        </SnackbarView>
      </Panels>
    </BrandsDashboardContext.Provider>
  );
};
